import React, { useState, useEffect } from 'react';
import {
  AlertTriangle,
  Shield,
  ChevronDown,
  ChevronUp,
  MessageSquare,
  CheckCircle,
  AlertCircle,
  Briefcase,
  XCircle,
  GitMerge,
  Clock,
  Send,
  Loader
} from 'lucide-react';
import { DetailedClauseJSON, ProblematicSubclause } from '../../../types/contract.types';
import DynamicIcon from '../../common/DynamicIcon';
import { clauseDecisionService, ClauseDecision, ClauseComment } from '../../../services/clauseDecisionService';

type ClauseStatus = 'pending' | 'agreed' | 'rejected' | 'compromise';

interface DetailedClauseReviewProps {
  clause: DetailedClauseJSON;
  contractId: string;
  analysisId: string;
  status?: ClauseStatus;
  currentUser?: string;
  onUpdate?: (clauseRef: string, updates: any) => void;
  onStatusChange?: (clauseRef: string, status: ClauseStatus) => void;
  onAddComment?: (clauseRef: string, comment: string) => void;
}

const DetailedClauseReview: React.FC<DetailedClauseReviewProps> = ({
  clause,
  contractId,
  analysisId,
  status = 'pending',
  currentUser = 'Current User',
  onUpdate,
  onStatusChange,
  onAddComment
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState('');
  const [localStatus, setLocalStatus] = useState<ClauseStatus>(status);
  const [dbDecision, setDbDecision] = useState<ClauseDecision | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmittingStatus, setIsSubmittingStatus] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load existing decision from database
  useEffect(() => {
    const loadDecision = async () => {
      if (!contractId || !analysisId || !clause.reference) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await clauseDecisionService.getClauseDecision(
          contractId,
          analysisId,
          clause.reference
        );

        if (response.data) {
          const decision = response.data as ClauseDecision;
          setDbDecision(decision);
          setLocalStatus(decision.status);
        }
      } catch (error) {
        console.error('Error loading clause decision:', error);
        setError('Failed to load decision data');
      } finally {
        setIsLoading(false);
      }
    };

    loadDecision();
  }, [contractId, analysisId, clause.reference]);

  const getRiskLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'high':
        return 'text-error-600 dark:text-error-400 bg-error-50 dark:bg-error-900/30 border-error-200 dark:border-error-800/50';
      case 'medium':
        return 'text-warning-600 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/30 border-warning-200 dark:border-warning-800/50';
      case 'low':
        return 'text-success-600 dark:text-success-400 bg-success-50 dark:bg-success-900/30 border-success-200 dark:border-success-800/50';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700';
    }
  };

  const getFavorabilityColor = (favorability: string) => {
    switch (favorability) {
      case 'favors counterparty':
        return 'text-error-600 dark:text-error-400 bg-error-50 dark:bg-error-900/30';
      case 'balanced':
        return 'text-warning-600 dark:text-warning-400 bg-warning-50 dark:bg-warning-900/30';
      case 'favors us':
        return 'text-success-600 dark:text-success-400 bg-success-50 dark:bg-success-900/30';
      default:
        return 'text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-800';
    }
  };

  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'deal-breaker':
      case 'dealbreaker':
        return 'bg-error-600 dark:bg-error-500 text-white';
      case 'negotiable':
        return 'bg-warning-500 dark:bg-warning-400 text-white dark:text-gray-900';
      case 'acceptable':
        return 'bg-success-600 dark:bg-success-500 text-white';
      default:
        return 'bg-gray-600 dark:bg-gray-500 text-white';
    }
  };

  const getStatusColor = (status: ClauseStatus) => {
    switch (status) {
      case 'agreed':
        return 'bg-success-100 dark:bg-success-900/30 text-success-800 dark:text-success-400 border-success-200 dark:border-success-800/50';
      case 'rejected':
        return 'bg-error-100 dark:bg-error-900/30 text-error-800 dark:text-error-400 border-error-200 dark:border-error-800/50';
      case 'compromise':
        return 'bg-warning-100 dark:bg-warning-900/30 text-warning-800 dark:text-warning-400 border-warning-200 dark:border-warning-800/50';
      case 'pending':
      default:
        return 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    }
  };

  const getStatusIcon = (status: ClauseStatus) => {
    switch (status) {
      case 'agreed':
        return <CheckCircle className="w-4 h-4" />;
      case 'rejected':
        return <XCircle className="w-4 h-4" />;
      case 'compromise':
        return <GitMerge className="w-4 h-4" />;
      case 'pending':
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const handleStatusChange = async (newStatus: ClauseStatus) => {
    if (!contractId || !analysisId || !clause.reference) return;

    setIsSubmittingStatus(true);
    setError(null);

    try {
      // Update database first
      const response = await clauseDecisionService.updateClauseDecision(
        contractId,
        analysisId,
        clause.reference,
        {
          status: newStatus,
          notes: comment.trim() || undefined
        }
      );

      if (response.data) {
        const decision = response.data as ClauseDecision;
        setDbDecision(decision);
        setLocalStatus(newStatus);

        // Call parent callbacks if provided (for backward compatibility)
        if (onStatusChange) {
          onStatusChange(clause.reference, newStatus);
        }
        if (onUpdate) {
          onUpdate(clause.reference, { status: newStatus });
        }
      }
    } catch (error) {
      console.error('Error updating clause status:', error);
      setError('Failed to update clause decision. Please try again.');
    } finally {
      setIsSubmittingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim() || !contractId || !analysisId || !clause.reference) return;

    setIsSubmittingComment(true);
    setError(null);

    try {
      // Use the new addClauseComment method for threading
      const response = await clauseDecisionService.addClauseComment(
        contractId,
        analysisId,
        clause.reference,
        comment.trim()
      );

      if (response.data) {
        const decision = response.data as ClauseDecision;
        setDbDecision(decision);

        // Call parent callback if provided (for backward compatibility)
        if (onAddComment) {
          onAddComment(clause.reference, comment);
        }

        setComment('');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      setError('Failed to add comment. Please try again.');
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const getRiskLevelBadge = (level: string) => {
    const colors = getRiskLevelColor(level);
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors}`}>
        {level === 'high' && <AlertTriangle className="w-3 h-3 mr-1" />}
        {level === 'medium' && <AlertCircle className="w-3 h-3 mr-1" />}
        {level === 'low' && <CheckCircle className="w-3 h-3 mr-1" />}
        {level} Risk
      </span>
    );
  };

  return (
    <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm dark:shadow-dark-lg mb-4 overflow-hidden border border-gray-200 dark:border-dark-border">
      {/* Clause Header */}
      <div
        className="p-5 bg-gray-50 dark:bg-dark-bg-secondary cursor-pointer hover:bg-gray-100 dark:hover:bg-dark-surface-raised transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            {/* Title Section */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-xl font-bold text-gray-800 dark:text-white">{clause.reference}</span>
              <span className="text-gray-400 dark:text-gray-500">—</span>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{clause.title}</h3>
              <span className={`ml-auto px-3 py-1 rounded-full text-sm font-medium ${getClassificationColor(clause.negotiation_analysis.classification)}`}>
                {clause.negotiation_analysis.classification.replace('-', ' ')}
              </span>
            </div>

            {/* Summary Section */}
            {clause.summary && (
              <div className="mb-3 flex items-start">
                <span className="text-lg mr-2 flex-shrink-0 text-primary-500 dark:text-accent-400">◆</span>
                <div>
                  <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Summary:</span>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 leading-relaxed">{clause.summary}</p>
                </div>
              </div>
            )}

            {/* Risk Indicators Row */}
            <div className="flex items-center gap-4 mt-3">
              {clause.icons.risk && (
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <DynamicIcon name={clause.icons.risk} className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">Risk: {clause.risk_assessment.legal_risk}</span>
                </div>
              )}
              {clause.icons.negotiation && (
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <DynamicIcon name={clause.icons.negotiation} className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">Negotiation Required</span>
                </div>
              )}
              {clause.icons.finance && (
                <div className="flex items-center text-gray-600 dark:text-gray-400">
                  <DynamicIcon name={clause.icons.finance} className="w-4 h-4 mr-1" />
                  <span className="text-sm font-medium">Financial Impact</span>
                </div>
              )}
            </div>
          </div>

          <div className="ml-4 text-gray-500 dark:text-gray-400">
            {isExpanded ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-dark-border">
          {/* Party Obligations Section */}
          <div className="p-5 bg-warning-50/50 dark:bg-warning-900/10 border-b border-warning-100 dark:border-warning-900/30">
            <div className="flex items-start">
              <span className="text-lg mr-2 flex-shrink-0 text-warning-500">📌</span>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-2">Party Obligations:</h4>
                <p className="text-sm text-gray-800 dark:text-gray-200 leading-relaxed">{clause.party_obligations}</p>
              </div>
            </div>
          </div>

          {/* Problematic Subclauses */}
          {clause.problematic_subclauses.length > 0 && (
            <div className="p-5 bg-error-50/50 dark:bg-error-900/10 border-b border-error-100 dark:border-error-900/30">
              <div className="flex items-start">
                <span className="text-lg mr-2 flex-shrink-0 text-error-500">➕</span>
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">Problem Subclauses:</h4>
                  <div className="space-y-3">
                    {clause.problematic_subclauses.map((subclause: ProblematicSubclause, index: number) => (
                      <div
                        key={index}
                        className="bg-white dark:bg-dark-surface p-4 rounded-lg border border-error-200 dark:border-error-800/50"
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-sm font-bold text-gray-700 dark:text-gray-300">{subclause.subref}</span>
                            <DynamicIcon name={subclause.icon} className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            {getRiskLevelBadge(subclause.risk_level)}
                          </div>
                        </div>
                        <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">{subclause.issue}</p>
                        <div className="flex items-start">
                          <span className="text-sm mr-1">💡</span>
                          <p className="text-sm text-gray-600 dark:text-gray-400 italic">{subclause.note}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Risk & Negotiation Analysis Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 p-5 bg-gray-50 dark:bg-dark-bg-secondary">
            {/* Risk Assessment */}
            <div className="bg-white dark:bg-dark-surface p-4 rounded-lg border border-gray-200 dark:border-dark-border">
              <h4 className="font-semibold text-gray-800 dark:text-white flex items-center mb-3">
                <Shield className="w-5 h-5 mr-2 text-primary-500 dark:text-accent-400" />
                Risk Assessment
              </h4>
              <div className="space-y-2 text-sm">
                {clause.risk_assessment.legal_risk && (
                  <div className="flex items-start">
                    <span className="font-medium text-gray-600 dark:text-gray-400 w-32">Legal Risk:</span>
                    <span className={`font-medium ${clause.risk_assessment.legal_risk.toLowerCase() === 'high' ? 'text-error-600 dark:text-error-400' : clause.risk_assessment.legal_risk.toLowerCase() === 'medium' ? 'text-warning-600 dark:text-warning-400' : 'text-success-600 dark:text-success-400'}`}>
                      {clause.risk_assessment.legal_risk}
                    </span>
                  </div>
                )}
                {clause.risk_assessment.financial_exposure && (
                  <div className="flex items-start">
                    <span className="font-medium text-gray-600 dark:text-gray-400 w-32">Financial:</span>
                    <span className="text-gray-800 dark:text-gray-200">{clause.risk_assessment.financial_exposure}</span>
                  </div>
                )}
                {clause.risk_assessment.operational_impact && (
                  <div className="flex items-start">
                    <span className="font-medium text-gray-600 dark:text-gray-400 w-32">Operational:</span>
                    <span className="text-gray-800 dark:text-gray-200">{clause.risk_assessment.operational_impact}</span>
                  </div>
                )}
                {clause.risk_assessment.regulatory_concerns && (
                  <div className="flex items-start">
                    <span className="font-medium text-gray-600 dark:text-gray-400 w-32">Regulatory:</span>
                    <span className="text-gray-800 dark:text-gray-200">{clause.risk_assessment.regulatory_concerns}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Negotiation Analysis */}
            <div className="bg-white dark:bg-dark-surface p-4 rounded-lg border border-gray-200 dark:border-dark-border">
              <h4 className="font-semibold text-gray-800 dark:text-white flex items-center mb-3">
                <Briefcase className="w-5 h-5 mr-2 text-primary-500 dark:text-accent-400" />
                Negotiation Analysis
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <span className="font-medium text-gray-600 dark:text-gray-400 w-32">Favorability:</span>
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getFavorabilityColor(clause.negotiation_analysis.favorability)}`}>
                    {clause.negotiation_analysis.favorability}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="font-medium text-gray-600 dark:text-gray-400 w-32">Market Standard:</span>
                  <span className="text-gray-800 dark:text-gray-200">{clause.negotiation_analysis.market_standard}</span>
                </div>
                {clause.negotiation_analysis.recommended_modification && (
                  <div className="mt-3 p-3 bg-warning-50 dark:bg-warning-900/20 rounded-lg border border-warning-200 dark:border-warning-800/50">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Recommended Modification:</p>
                    <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">{clause.negotiation_analysis.recommended_modification}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Actionable Recommendations */}
          <div className="p-5 bg-primary-50/50 dark:bg-primary-900/10 border-t border-primary-100 dark:border-primary-900/30">
            <div className="flex items-start">
              <span className="text-lg mr-2 flex-shrink-0">🛠️</span>
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">Immediate Actions:</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {clause.actionable_recommendations.immediate_actions && (
                    <div className="bg-white dark:bg-dark-surface p-3 rounded-lg border border-primary-200 dark:border-primary-800/50">
                      <h5 className="text-xs font-semibold text-primary-700 dark:text-primary-400 mb-1">Actions Required</h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{clause.actionable_recommendations.immediate_actions}</p>
                    </div>
                  )}
                  {clause.actionable_recommendations.approvals_needed && (
                    <div className="bg-white dark:bg-dark-surface p-3 rounded-lg border border-primary-200 dark:border-primary-800/50">
                      <h5 className="text-xs font-semibold text-primary-700 dark:text-primary-400 mb-1">Approvals Needed</h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{clause.actionable_recommendations.approvals_needed}</p>
                    </div>
                  )}
                  {clause.actionable_recommendations.implementation_notes && (
                    <div className="bg-white dark:bg-dark-surface p-3 rounded-lg border border-primary-200 dark:border-primary-800/50">
                      <h5 className="text-xs font-semibold text-primary-700 dark:text-primary-400 mb-1">Implementation Notes</h5>
                      <p className="text-sm text-gray-700 dark:text-gray-300">{clause.actionable_recommendations.implementation_notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Status and Comments Section */}
          <div className="p-5 bg-gray-50 dark:bg-dark-bg-secondary border-t border-gray-200 dark:border-dark-border">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/50 rounded-lg">
                <div className="flex items-center">
                  <AlertCircle className="w-4 h-4 text-error-600 dark:text-error-400 mr-2 flex-shrink-0" />
                  <span className="text-sm text-error-700 dark:text-error-400">{error}</span>
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="mb-4 p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800/50 rounded-lg">
                <div className="flex items-center">
                  <Loader className="w-4 h-4 text-primary-600 dark:text-primary-400 mr-2 animate-spin flex-shrink-0" />
                  <span className="text-sm text-primary-700 dark:text-primary-400">Loading decision data...</span>
                </div>
              </div>
            )}


            {/* Status Buttons */}
            <div className="mb-4">
              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider mb-3">
                Clause Decision
              </h4>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  onClick={() => handleStatusChange('agreed')}
                  disabled={isSubmittingStatus}
                  className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                    isSubmittingStatus
                      ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                      : localStatus === 'agreed'
                      ? 'bg-success-600 dark:bg-success-500 text-white shadow-lg'
                      : 'bg-white dark:bg-dark-surface text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border hover:bg-success-50 dark:hover:bg-success-900/20 hover:border-success-300 dark:hover:border-success-800/50'
                  }`}
                >
                  {isSubmittingStatus ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  Agree
                </button>

                <button
                  onClick={() => handleStatusChange('rejected')}
                  disabled={isSubmittingStatus}
                  className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                    isSubmittingStatus
                      ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                      : localStatus === 'rejected'
                      ? 'bg-error-600 dark:bg-error-500 text-white shadow-lg'
                      : 'bg-white dark:bg-dark-surface text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border hover:bg-error-50 dark:hover:bg-error-900/20 hover:border-error-300 dark:hover:border-error-800/50'
                  }`}
                >
                  {isSubmittingStatus ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <XCircle className="w-4 h-4" />
                  )}
                  Reject
                </button>

                <button
                  onClick={() => handleStatusChange('compromise')}
                  disabled={isSubmittingStatus}
                  className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                    isSubmittingStatus
                      ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                      : localStatus === 'compromise'
                      ? 'bg-warning-600 dark:bg-warning-500 text-white shadow-lg'
                      : 'bg-white dark:bg-dark-surface text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border hover:bg-warning-50 dark:hover:bg-warning-900/20 hover:border-warning-300 dark:hover:border-warning-800/50'
                  }`}
                >
                  {isSubmittingStatus ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <GitMerge className="w-4 h-4" />
                  )}
                  Compromise
                </button>

                <button
                  onClick={() => handleStatusChange('pending')}
                  disabled={isSubmittingStatus}
                  className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                    isSubmittingStatus
                      ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                      : localStatus === 'pending'
                      ? 'bg-gray-600 dark:bg-gray-500 text-white shadow-lg'
                      : 'bg-white dark:bg-dark-surface text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-dark-surface-raised hover:border-gray-400 dark:hover:border-gray-600'
                  }`}
                >
                  {isSubmittingStatus ? (
                    <Loader className="w-4 h-4 animate-spin" />
                  ) : (
                    <Clock className="w-4 h-4" />
                  )}
                  Pending
                </button>

                {/* Current Status Badge */}
                <div className="ml-auto flex items-center gap-2">
                  <span className="text-sm text-gray-600 dark:text-gray-400">Current Status:</span>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(localStatus)}`}>
                    {getStatusIcon(localStatus)}
                    <span className="ml-1 capitalize">{localStatus}</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Comments Section - Messaging App Style */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                  Discussion Thread ({dbDecision?.comments.length || 0})
                </h4>
              </div>

              {/* Comments Thread */}
              {dbDecision?.comments && dbDecision.comments.length > 0 && (
                <div className="mb-4 bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-dark-border p-4">
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {dbDecision.comments
                      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
                      .map((comment) => (
                        <div key={comment._id} className="flex flex-col bg-gray-50 dark:bg-dark-bg-secondary rounded-lg p-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-primary-600 dark:bg-accent-500 rounded-full flex items-center justify-center text-white dark:text-gray-900 text-sm font-medium">
                                {comment.author.firstName?.[0] || '?'}{comment.author.lastName?.[0] || ''}
                              </div>
                              <div>
                                <span className="font-medium text-sm text-gray-900 dark:text-white">
                                  {comment.author.firstName} {comment.author.lastName}
                                </span>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  {new Date(comment.createdAt).toLocaleString()}
                                  {comment.isEdited && <span className="ml-1">(edited)</span>}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="ml-10 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                            {comment.text}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Comment Input */}
              <div className="bg-white dark:bg-dark-surface rounded-lg border border-gray-200 dark:border-dark-border p-3">
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add to the discussion..."
                  className="w-full p-3 text-sm bg-white dark:bg-dark-bg-secondary text-gray-900 dark:text-white border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-accent-400/20 focus:border-primary-500 dark:focus:border-accent-400 resize-none placeholder-gray-400 dark:placeholder-gray-500"
                  rows={3}
                />
                <div className="flex justify-end mt-3">
                  <button
                    onClick={handleAddComment}
                    disabled={!comment.trim() || isSubmittingComment}
                    className={`px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2 transition-all ${
                      isSubmittingComment
                        ? 'bg-gray-400 dark:bg-gray-600 text-gray-200 dark:text-gray-400 cursor-not-allowed'
                        : comment.trim()
                        ? 'bg-primary-600 dark:bg-accent-500 text-white dark:text-gray-900 hover:bg-primary-700 dark:hover:bg-accent-400 shadow-sm'
                        : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isSubmittingComment ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                    {isSubmittingComment ? 'Posting...' : 'Post Comment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DetailedClauseReview; 